import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import ManualIssueDialog from "../../components/ManualIssueDialog";
import {
  Users,
  Search,
  Download,
  Loader2,
  CheckCircle2,
  ArrowRight,
  RefreshCw,
  Wand2,
  Plus,
} from "lucide-react";
import {
  eventsApi,
  reportsApi,
  tokensApi,
  attendeesCsvUrl,
  centsToAUD,
  formatAuDate,
} from "../../lib/events";
import { extractError } from "../../lib/api";

const STATUS_LABEL = {
  valid: "Not checked in",
  checked_in: "Checked in",
  refunded: "Refunded",
  voided: "Voided",
  cancelled: "Cancelled",
};

const STATUS_TONE = {
  valid: "bg-emerald-100 text-emerald-900 border-emerald-500",
  checked_in: "bg-[var(--gt-ink)] text-white border-[var(--gt-ink)]",
  refunded: "bg-[var(--gt-accent)] text-white border-[var(--gt-accent)]",
  voided: "bg-neutral-200 text-neutral-700 border-neutral-400",
  cancelled: "bg-neutral-200 text-neutral-700 border-neutral-400",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "checked_in", label: "Checked in" },
  { key: "valid", label: "Not checked in" },
  { key: "refunded_voided", label: "Refunded / voided" },
];

function StatusBadge({ status }) {
  const label = STATUS_LABEL[status] || status;
  const tone =
    STATUS_TONE[status] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  return (
    <span
      className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm border ${tone}`}
      data-testid={`attendee-status-${status}`}
    >
      {label}
    </span>
  );
}

export default function AttendeesDashboard() {
  const [events, setEvents] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingAttendees, setLoadingAttendees] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [showManualIssue, setShowManualIssue] = useState(false);

  // Load organiser's events
  useEffect(() => {
    let alive = true;
    setLoadingList(true);
    eventsApi
      .list()
      .then((list) => {
        if (!alive) return;
        setEvents(list);
        // Default to first event with sales, else first event
        const preferred =
          list.find((e) => (e.ticket_types_summary?.total_sold || 0) > 0) ||
          list[0];
        if (preferred) setSelectedEventId(preferred.id);
      })
      .catch((e) => alive && setError(extractError(e)))
      .finally(() => alive && setLoadingList(false));
    return () => {
      alive = false;
    };
  }, []);

  const loadAttendees = useCallback(async () => {
    if (!selectedEventId) return;
    setLoadingAttendees(true);
    try {
      const rows = await reportsApi.attendees(selectedEventId);
      setAttendees(rows);
      setError("");
    } catch (e) {
      setError(extractError(e));
      setAttendees([]);
    } finally {
      setLoadingAttendees(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    loadAttendees();
  }, [loadAttendees]);

  const filtered = useMemo(() => {
    let rows = attendees;
    if (filter === "checked_in") {
      rows = rows.filter((t) => t.status === "checked_in");
    } else if (filter === "valid") {
      rows = rows.filter((t) => t.status === "valid");
    } else if (filter === "refunded_voided") {
      rows = rows.filter((t) =>
        ["refunded", "voided", "cancelled"].includes(t.status),
      );
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      rows = rows.filter(
        (t) =>
          (t.buyer_name || "").toLowerCase().includes(s) ||
          (t.buyer_email || "").toLowerCase().includes(s) ||
          (t.attendee_name || "").toLowerCase().includes(s) ||
          (t.attendee_email || "").toLowerCase().includes(s) ||
          (t.code || "").toLowerCase().includes(s) ||
          (t.ticket_type_name || "").toLowerCase().includes(s),
      );
    }
    return rows;
  }, [attendees, q, filter]);

  const counts = useMemo(() => {
    const out = {
      total: attendees.length,
      checked_in: 0,
      valid: 0,
      refunded_voided: 0,
    };
    attendees.forEach((t) => {
      if (t.status === "checked_in") out.checked_in += 1;
      else if (t.status === "valid") out.valid += 1;
      else if (["refunded", "voided", "cancelled"].includes(t.status))
        out.refunded_voided += 1;
    });
    return out;
  }, [attendees]);

  const noEvents = !loadingList && events && events.length === 0;

  return (
    <section className="p-6 sm:p-10" data-testid="attendees-dashboard-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="gt-label mb-2">Attendees</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">
            Ticket holders
          </h2>
          <p className="text-[var(--gt-ink-2)] text-sm mt-2">
            Pick an event to see attendees, QR scan status and export a door list.
          </p>
        </div>

        {loadingList && (
          <div
            className="flex items-center gap-3 text-[var(--gt-ink-2)]"
            data-testid="attendees-loading-events"
          >
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Loading your events…</span>
          </div>
        )}

        {noEvents && (
          <div
            className="gt-card-hard text-center p-10"
            data-testid="attendees-no-events"
          >
            <Users size={32} className="mx-auto text-[var(--gt-ink-3)] mb-3" strokeWidth={1.5} />
            <div className="font-heading text-xl font-bold mb-2">No events yet.</div>
            <p className="text-sm text-[var(--gt-ink-2)] max-w-md mx-auto mb-6">
              Create your first event and start selling tickets. Your attendees will show up here.
            </p>
            <Link
              to="/dashboard/events/new"
              className="gt-btn-primary inline-flex"
              data-testid="attendees-empty-cta"
            >
              Create event <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {events && events.length > 0 && (
          <>
            {showManualIssue && (
              <ManualIssueDialog
                open={showManualIssue}
                onClose={() => setShowManualIssue(false)}
                presetOrganiserId={events.find((e) => e.id === selectedEventId)?.organiser_id || ""}
                presetEventId={selectedEventId}
                onIssued={() => loadAttendees()}
              />
            )}
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-6" data-testid="attendees-event-picker-row">
              <div className="flex-1">
                <label className="gt-form-label">Event</label>
                <select
                  className="gt-input"
                  value={selectedEventId}
                  onChange={(e) => setSelectedEventId(e.target.value)}
                  data-testid="attendees-event-picker"
                >
                  {events.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.title} · {formatAuDate(e.start_at)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={loadAttendees}
                  className="gt-btn-ghost text-sm"
                  data-testid="attendees-refresh-btn"
                >
                  <RefreshCw size={14} /> Refresh
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setShowManualIssue(true)}
                    className="gt-btn-primary text-sm py-2"
                    data-testid="attendees-issue-ticket-btn"
                  >
                    <Plus size={14} /> Add Attendee / Issue Ticket
                  </button>
                )}
                {selectedEventId && (
                  <a
                    href={attendeesCsvUrl(selectedEventId)}
                    className="gt-btn-primary text-sm py-2"
                    data-testid="attendees-csv-btn"
                  >
                    <Download size={14} /> CSV
                  </a>
                )}
                {selectedEventId && (() => {
                  const ev = (events || []).find((x) => x.id === selectedEventId);
                  return ev?.token_numbers_enabled ? (
                    <button
                      type="button"
                      className="gt-btn-ghost text-sm"
                      data-testid="attendees-backfill-tokens-btn"
                      onClick={async () => {
                        try {
                          const dry = await tokensApi.backfill(selectedEventId, { dry_run: true });
                          const proceed = window.confirm(
                            `Backfill will assign ${dry.would_assign} token(s). ${dry.skipped_existing} tickets already have tokens. Proceed?`
                          );
                          if (!proceed) return;
                          const res = await tokensApi.backfill(selectedEventId, { dry_run: false });
                          alert(`Assigned ${res.assigned}; skipped ${res.skipped_existing} with existing tokens.`);
                          loadAttendees();
                        } catch (e) {
                          alert(e?.response?.data?.detail || "Backfill failed");
                        }
                      }}
                    >
                      <Wand2 size={14} /> Backfill tokens
                    </button>
                  ) : null;
                })()}
              </div>
            </div>

            {/* Summary counts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="attendees-summary">
              <div className="gt-card" data-testid="attendees-count-total">
                <div className="gt-label mb-1">Total tickets</div>
                <div className="font-heading text-2xl font-black tracking-tighter">
                  {counts.total}
                </div>
              </div>
              <div className="gt-card" data-testid="attendees-count-checked-in">
                <div className="gt-label mb-1">Checked in</div>
                <div className="font-heading text-2xl font-black tracking-tighter">
                  {counts.checked_in}
                </div>
              </div>
              <div className="gt-card" data-testid="attendees-count-valid">
                <div className="gt-label mb-1">Not checked in</div>
                <div className="font-heading text-2xl font-black tracking-tighter">
                  {counts.valid}
                </div>
              </div>
              <div className="gt-card" data-testid="attendees-count-void">
                <div className="gt-label mb-1">Refunded / voided</div>
                <div className="font-heading text-2xl font-black tracking-tighter">
                  {counts.refunded_voided}
                </div>
              </div>
            </div>

            {/* Filter + Search */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="flex gap-2 flex-wrap" data-testid="attendees-filter-group">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFilter(f.key)}
                    className={`px-3 py-2 text-xs uppercase tracking-widest border rounded-sm transition-colors ${
                      filter === f.key
                        ? "bg-[var(--gt-ink)] text-white border-[var(--gt-ink)]"
                        : "border-[var(--gt-line)] text-[var(--gt-ink-2)] hover:border-[var(--gt-ink)]"
                    }`}
                    data-testid={`attendees-filter-${f.key}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gt-ink-3)]"
                />
                <input
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, ticket code…"
                  className="gt-input pl-9"
                  data-testid="attendees-search"
                />
              </div>
            </div>

            {loadingAttendees && (
              <div
                className="flex items-center gap-3 text-[var(--gt-ink-2)]"
                data-testid="attendees-loading"
              >
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Loading attendees…</span>
              </div>
            )}

            {error && (
              <div
                className="gt-card border-[var(--gt-accent)] text-[var(--gt-accent)]"
                data-testid="attendees-error"
              >
                {error}
              </div>
            )}

            {!loadingAttendees && !error && attendees.length === 0 && (
              <div
                className="gt-card-hard text-center p-10"
                data-testid="attendees-empty"
              >
                <Users size={28} className="mx-auto text-[var(--gt-ink-3)] mb-3" strokeWidth={1.5} />
                <div className="font-heading text-xl font-bold mb-2">
                  No attendees yet.
                </div>
                <p className="text-sm text-[var(--gt-ink-2)] max-w-md mx-auto">
                  Once tickets are sold for this event, ticket holders will appear here.
                </p>
              </div>
            )}

            {!loadingAttendees && filtered.length > 0 && (
              <div className="gt-card overflow-x-auto" data-testid="attendees-table-wrap">
                <table className="w-full text-sm" data-testid="attendees-table">
                  <thead>
                    <tr className="text-left border-b border-[var(--gt-line)]">
                      <th className="py-2 pr-3 gt-label">Name</th>
                      <th className="py-2 px-3 gt-label">Guest</th>
                      <th className="py-2 px-3 gt-label">Ticket type</th>
                      <th className="py-2 px-3 gt-label">Token</th>
                      <th className="py-2 px-3 gt-label">Seat</th>
                      <th className="py-2 px-3 gt-label">Status</th>
                      <th className="py-2 px-3 gt-label">Order</th>
                      <th className="py-2 px-3 gt-label text-right">Price</th>
                      <th className="py-2 pl-3 gt-label">Ticket code</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((t) => (
                      <tr
                        key={t.code}
                        className="border-b border-[var(--gt-line)]/60 hover:bg-[var(--gt-bg-surface)]"
                        data-testid={`attendee-row-${t.code}`}
                      >
                        <td className="py-3 pr-3">
                          <div className="font-semibold text-[var(--gt-ink)]">
                            {t.buyer_name || "—"}
                          </div>
                          <div className="text-xs text-[var(--gt-ink-3)]">
                            {t.buyer_email || ""}
                          </div>
                        </td>
                        <td className="py-3 px-3" data-testid={`attendee-guest-${t.code}`}>
                          {t.attendee_name ? (
                            <>
                              <div className="text-[var(--gt-ink)]">{t.attendee_name}</div>
                              <div className="text-xs text-[var(--gt-ink-3)]">
                                {t.attendee_email || t.attendee_phone || ""}
                              </div>
                            </>
                          ) : (
                            <span className="text-[var(--gt-ink-3)]">—</span>
                          )}
                        </td>
                        <td className="py-3 px-3">{t.ticket_type_name || "—"}</td>
                        <td className="py-3 px-3 font-mono text-xs" data-testid={`attendee-token-${t.code}`}>
                          {t.token_number ? (
                            <span
                              className="cursor-pointer hover:underline"
                              data-testid={`attendee-token-edit-${t.code}`}
                              onClick={async () => {
                                const next = window.prompt("New token (blank to clear):", t.token_number || "");
                                if (next === null) return;
                                try {
                                  await tokensApi.patch(t.id || t.ticket_id, next.trim() || null);
                                  loadAttendees();
                                } catch (e) {
                                  alert(e?.response?.data?.detail || "Update failed");
                                }
                              }}
                            >
                              {t.token_number}
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)] text-xs underline"
                              data-testid={`attendee-token-assign-${t.code}`}
                              onClick={async () => {
                                const next = window.prompt("Enter token (e.g. FOOD-001):", "");
                                if (!next) return;
                                try {
                                  await tokensApi.patch(t.id || t.ticket_id, next.trim());
                                  loadAttendees();
                                } catch (e) {
                                  alert(e?.response?.data?.detail || "Assign failed");
                                }
                              }}
                            >
                              —
                            </button>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-xs">
                          {t.seat_label
                            ? `${t.section || ""}${t.section ? " · " : ""}${t.seat_label}`
                            : "—"}
                        </td>
                        <td className="py-3 px-3">
                          <StatusBadge status={t.status || "valid"} />
                          {t.checked_in_at && (
                            <div className="text-[10px] mt-1 text-[var(--gt-ink-3)] flex items-center gap-1">
                              <CheckCircle2 size={10} /> {formatAuDate(t.checked_in_at)}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 font-mono text-xs text-[var(--gt-ink-2)]">
                          {(t.order_id || "").slice(0, 8)}
                        </td>
                        <td className="py-3 px-3 text-right font-mono">
                          {centsToAUD(t.price_cents || 0)}
                        </td>
                        <td className="py-3 pl-3 font-mono text-xs">{t.code}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loadingAttendees && attendees.length > 0 && filtered.length === 0 && (
              <div
                className="text-sm text-[var(--gt-ink-2)] mt-4"
                data-testid="attendees-no-match"
              >
                No attendees match your filter.
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
